import React from "react";
import { createContext, useContext } from "react";
import { SiteTextEntry } from "@Front/Stores/SiteTextsStore";
import {
	useSiteTextsContextApi,
	useSiteTextsNormalizedLists,
	useSiteTextsNormalizedLocale,
	useSiteTextsPreloadCacheKey,
	useSiteTextsPreloadEffect,
} from "@Front/Stores/siteTextsProviderHooks";

type SiteTextsContextValue = {
	locale: string;
	getEntry: (textKey: string) => SiteTextEntry;
	getContent: (textKey: string) => string;
	has: (textKey: string) => boolean;
};

const SiteTextsContext = createContext<SiteTextsContextValue | null>(null);

type SiteTextsProviderProps = {
	scopes?: string[];
	textKeys?: string[];
	locale?: string;
	children: React.ReactNode;
};

export function SiteTextsProvider({
	scopes = [],
	textKeys = [],
	locale: localeProp = "fr-FR",
	children,
}: SiteTextsProviderProps): React.JSX.Element {
	const locale = useSiteTextsNormalizedLocale(localeProp);
	const { normalizedScopes, normalizedTextKeys } = useSiteTextsNormalizedLists(scopes, textKeys);
	const preloadCacheKey = useSiteTextsPreloadCacheKey(locale, normalizedScopes, normalizedTextKeys);
	const { isLoading, error } = useSiteTextsPreloadEffect(locale, normalizedScopes, normalizedTextKeys, preloadCacheKey);
	const value = useSiteTextsContextApi(locale, isLoading, error);

	return <SiteTextsContext.Provider value={value}>{children}</SiteTextsContext.Provider>;
}

export function useSiteText(textKey: string): SiteTextEntry {
	const context = useContext(SiteTextsContext);
	if (!context) {
		throw new Error("useSiteText must be used within a SiteTextsProvider");
	}

	return context.getEntry(textKey);
}

export function useSiteTextContent(textKey: string): string {
	return useSiteText(textKey).content;
}

/** Résolution de libellés par clé dynamique (hors `useSiteTextContent` statique). */
export function useSiteTextGetter(): (textKey: string) => string {
	const context = useContext(SiteTextsContext);
	if (!context) {
		throw new Error("useSiteTextGetter must be used within a SiteTextsProvider");
	}
	return context.getContent;
}
